import { useEffect } from "react";
import { useSelector } from "react-redux";
import { getDealId, getEncompassLoanId, getLoanNumber, getNhDealApplicationId, getCurrentUserName, getProspectId } from "features/authSlice";
import { ESignFSEventType, NeedItem, NeedStatus, NeedType, useMarkESignFSEventSentMutation } from "pages/dashboard/home/dashboardService";
import { getLoanProgress } from "pages/dashboard/store/dashboardSlice";
import { trackFSESignCompleted, trackFSESignCreated, trackFSESignStarted, sessionESignEventKeys } from "utilities/full-story";
import { CLOSING_DISCLOSURE_TITLE_PREFIX, DISCLOSURE_TITLE_PREFIX } from "utilities/Constant";

interface UseESignFSTrackingOptions {
  /** Also track the "E-Sign Created" event when needStatus is New. TaskCard uses this; Tasks.tsx does not. */
  trackCreated?: boolean;
}

/**
 * Tracks E-Sign FullStory events (Created, Started, Completed) for a given needs list.
 * Fires the FullStory event then calls the markESignFSEventSent API to persist the flag.
 * Guards against duplicate in-flight calls with an internal ref.
 */
export function useESignFSTracking(
  needItems: NeedItem[] | undefined,
  options: UseESignFSTrackingOptions = {},
) {
  const { trackCreated = false } = options;

  const username = useSelector(getCurrentUserName);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const prospectId = useSelector(getProspectId);
  const dealId = useSelector(getDealId);
  const loanNumber = useSelector(getLoanNumber);
  const losGuid = useSelector(getEncompassLoanId);
  const loanProgress = useSelector(getLoanProgress);

  const [markESignFSEventSent] = useMarkESignFSEventSentMutation();

  useEffect(() => {
    try {
      if (!needItems?.length || !nhDealApplicationId) return;

      const esignItems = needItems
        .filter((item) => item.needType === NeedType.ESign && item.isEnabled && !!item.title && !!item.packageId)
        .map((item) => {
          const normalizedTitle = item.title.replace(/\s/g, "").toLowerCase();
          let titlePrefix: string | undefined;
          if (normalizedTitle.startsWith(DISCLOSURE_TITLE_PREFIX)) {
            titlePrefix = DISCLOSURE_TITLE_PREFIX;
          } else if (normalizedTitle.startsWith(CLOSING_DISCLOSURE_TITLE_PREFIX)) {
            titlePrefix = CLOSING_DISCLOSURE_TITLE_PREFIX;
          }
          return { item, titlePrefix };
        })
        .filter(({ titlePrefix }) => !!titlePrefix) as { item: NeedItem; titlePrefix: string }[];

      if (!esignItems.length) return;

      const trackEvent = async (item: NeedItem, titlePrefix: string, eventType: ESignFSEventType) => {
        const eventKey = [
          nhDealApplicationId,
          item.prospectId ?? prospectId,
          item.packageId,
          titlePrefix,
          eventType,
        ].join(":");
        if (sessionESignEventKeys.has(eventKey)) {
          return;
        }
        sessionESignEventKeys.add(eventKey);

        const baseProps = {
          username,
          prospectId: item.prospectId ?? prospectId,
          dealId: dealId || null,
          timestamp: new Date().toISOString(),
          losLoanNumber: loanNumber || null,
          losGuid: losGuid || null,
          loanMilestone: loanProgress?.status ?? "Unknown",
        };

        try {
          if (eventType === ESignFSEventType.Created) {
            trackFSESignCreated(baseProps);
          } else if (eventType === ESignFSEventType.Started) {
            trackFSESignStarted(baseProps);
          } else {
            trackFSESignCompleted(baseProps);
          }

          await markESignFSEventSent({
            nhDealApplicationId,
            prospectId: item.prospectId ?? prospectId,
            packageId: item.packageId!,
            titlePrefix,
            eventType,
          }).unwrap();
        } catch {
          sessionESignEventKeys.delete(eventKey);
        }
      };

      esignItems.forEach(({ item, titlePrefix }) => {
          if (
            trackCreated &&
            item.isEnabled &&
            item.needStatus?.toLowerCase() === NeedStatus.New.toLowerCase() &&
            !item.isEsignCreatedFSEventSent
          ) {
            void trackEvent(item, titlePrefix, ESignFSEventType.Created);
          }

          if (
            item.isEnabled &&
            item.needStatus?.toLowerCase() === NeedStatus.Completed.toLowerCase() &&
            !item.isEsignCompletedFSEventSent
          ) {
            // If Started event was never sent (task signed without an explicit CTA click),
            // send Started retroactively first, then Completed.
            if (item.isEsignStartedFSEventSent) {
              void trackEvent(item, titlePrefix, ESignFSEventType.Completed);
            } else {
              void (async () => {
                await trackEvent(item, titlePrefix, ESignFSEventType.Started);
                await trackEvent(item, titlePrefix, ESignFSEventType.Completed);
              })();
            }
          }
        });
    } catch {
      return;
    }
  }, [needItems]);
}
